import { MAX_NAME_LENGTH } from '../constants/shared'

/**
 * Sanitizes player name allowing only safe, aesthetic characters
 * @param {string} name - Raw player name input
 * @returns {string} - Sanitized name with only allowed characters
 */

// TODO (v1.2.5+):
// Current sanitization behavior conflates two different cases:
//
// Case 1 (should auto-fix): Multiple spaces
//   Input:  'Diego   Armando'
//   Current: ❌ "Invalid characters" error
//   Desired: ✅ Auto-fix to 'Diego Armando'
//
// Case 2 (should reject): Invalid characters (emojis, symbols)
//   Input:  'Diego❤️Armando'
//   Current: ❌ "Invalid characters" error (correct, but same error as Case 1)
//   Desired: ✅ Explicit "Invalid characters" error (differentiated from Case 1)
//
// Refactor needed:
// 1. Separate space normalization from character validation
// 2. Normalize spaces first (always applied, never errors)
// 3. Validate characters second (reject if invalid chars present)
// 4. Return specific error messages for each validation failure
//
// Impact: Better UX (auto-fix spaces vs explicit error for symbols)

function sanitizePlayerName (name) {
  // 🛡️ DEFENSIVE: Allow only letters, numbers, spaces, and basic punctuation
  // Preserves international characters while blocking nonsensical symbols
  return name
    .replace(/[^a-zA-Z0-9\u00C0-\u017F\u1E00-\u1EFF\s\-.']/g, '') // Keep letters, numbers, spaces, accents, hyphens, periods, apostrophes
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim()
}

export function validatePlayerName (name, currentPlayers, maxPlayers, t) {
  // 🛡️ SECURITY: Sanitize input before any processing
  const trimmedName = name.trim()
  const sanitized = sanitizePlayerName(trimmedName)

  if (sanitized !== trimmedName) {
    return {
      valid: false,
      error: t.shared.player.errors.invalidCharacters
    }
  }

  if (!sanitized) {
    return { valid: false, error: t.shared.player.errors.empty }
  }

  if (sanitized.length > MAX_NAME_LENGTH) {
    return {
      valid: false,
      error: t.shared.player.errors.tooLong.replace('{max}', MAX_NAME_LENGTH)
    }
  }

  if (currentPlayers.length >= maxPlayers) {
    return {
      valid: false,
      error: t.shared.player.errors.full.replace('{max}', maxPlayers)
    }
  }

  if (currentPlayers.some(p => p.name.toLowerCase() === sanitized.toLowerCase())) {
    return { valid: false, error: t.shared.player.errors.duplicate }
  }

  return { valid: true, name: sanitized }
}
